import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AdvancedForm } from '@components/admission-modal/admission/advanced-form';
import { FIELD_QUANTITY, REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { IconBucket } from '@components/icons';
import { Input } from '@components/input';
import { InputCheckbox } from '@components/input-checkbox';
import { InputPrice } from '@components/input-price';
import {
  CHANNELS_SALES_QUOTA_CHANNEL,
  CHANNELS_SALES_QUOTA_UNLIMITED,
} from '@components/input-select-channels/src/input-select-channels.type';
import { LabelPrimary } from '@components/label';
import { LabelInfo } from '@components/label/src/label-info';
import { Listbox } from '@components/listbox';
import { TextareaCounter } from '@components/textarea-counter';
import { DEFAULT_MAX_ITEMS_PER_TRANSACTION, DESCRIPTION_MAX_LENGTH } from '@constants/admissions';
import { DRINKS_INCLUDED_CLUB_IDS } from '@constants/drinksincluded-club-ids';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ADMISSION_TYPE_BOTTLE_SERVICE, ADMISSION_TYPE_GUEST_LIST, Translations } from '@domain/admission';
import { ActiveChannelRequest, Channel } from '@domain/channel';
import { LANGUAGES } from '@domain/language';
import { CreateAdmissionPresetRequest, PRESET_TYPE_BOTTLE_SERVICE, PRESET_TYPE_TICKET } from '@domain/preset';
import { useGetCompanyVatRates } from '@hooks/company/use-get-company-vat-rates';
import { useGetCompanyClubChannels } from '@hooks/company-club/use-get-company-channels';
import { useGetPresetActiveChannels } from '@hooks/presets/admissions/use-get-admission-preset-channels';
import { DATA_TESTID_ADMISSION_MODAL_TICKET_STEP_TITLE } from '@tests/constants/data-testid';
import { calculateMaxItemsPerTransation } from '@utils/calculate-max-items-per-transation/calculate-max-items-per-transation';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getDirtyFields } from '@utils/get-dirty-fields';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';
import { formatCurrency } from '@utils/price-formatter/price-formatter';
import { cannotBeNegative } from '@utils/validations/cannot-be-negative';
import { max } from '@utils/validations/max';
import { maxDecimals } from '@utils/validations/max-decimals';
import { min } from '@utils/validations/min';

import { ColorPickerModal } from '../../color-picker-modal';
import { ConfirmModal } from '../../confirm-modal';
import { EditFieldModal } from '../../edit-field-modal';
import { ModalStickyFooter } from '../../modal-sticky-footer';
import { ModalStickyHeader } from '../../modal-sticky-header';
import { ModalV2 } from '../../modal-v2';

import { Form, Props } from './create-preset.type';
import { getDefaultValues, getTicketFromForm } from './utils';

export const CreatePreset = ({
  handleClose,
  handleSubmit: handleFormSubmit,
  icon,
  iconClassName,
  formFieldsPresets,
  type,
  preset,
  floorplan,
  isConfirmModalOpen,
  handleCloseConfirmModal,
  handleSaveConfirmModal,
  setActiveConfirmModal,
}: Props) => {
  const { clubState, companyClubState, companyState } = useContext(CompanyClubContext);
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [offerColor, setOfferColor] = useState(preset?.color ?? '');
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [acceptDownPayment, setAcceptDownPayment] = useState<boolean>(!!preset?.price.offlinePrice);
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const isClubCountryItaly = clubState.city.country.isoCode === 'ITA';
  const isTicketOrBottleService = type === PRESET_TYPE_BOTTLE_SERVICE || type === PRESET_TYPE_TICKET;
  const isDdpActive = isClubCountryItaly && isTicketOrBottleService;
  const isDrinksIncludedActive = DRINKS_INCLUDED_CLUB_IDS.includes(clubState.id);
  const gridWidth = isDdpActive || isDrinksIncludedActive ? 'grid-cols-2' : 'grid-cols-3';

  const venueCurrency = clubState.city.country.currency;

  const { data: companyChannels } = useGetCompanyClubChannels({ companyClubId: companyClubState.id });
  const { data: activeChannels } = useGetPresetActiveChannels({
    presetId: preset?.id,
    enabled: !!preset?.id,
  });
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [admissionChannels, setAdmissionChannels] = useState<Channel[]>([]);
  const { data: companyVatRates } = useGetCompanyVatRates({
    companyId: companyState.id,
    options: { enabled: !!companyState.id },
  });

  const methods = useForm<Form>({
    defaultValues: getDefaultValues({
      locale: i18n.language,
      preset,
      formFieldsPresets,
      type,
      company: companyState,
    }),
    mode: 'onChange',
  });

  const {
    clearErrors,
    setError,
    setValue,
    getValues,
    watch,
    register,
    control,
    formState: { errors, isValid, dirtyFields },
    handleSubmit,
  } = methods;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
  // @ts-ignore
  watch(() => {
    if (setActiveConfirmModal) setActiveConfirmModal(JSON.stringify(dirtyFields) !== JSON.stringify({}));
  });

  // Preload VAT rate based on the rules
  useEffect(() => {
    if (!preset && companyVatRates) {
      // For new presets, use company VAT rate based on type
      if (type === PRESET_TYPE_TICKET && companyVatRates.ticketVatRate != null) {
        setValue('vatRate', companyVatRates.ticketVatRate.toString(), { shouldDirty: false });
      } else if (type === PRESET_TYPE_BOTTLE_SERVICE && companyVatRates.bottleServiceVatRate != null) {
        setValue('vatRate', companyVatRates.bottleServiceVatRate.toString(), { shouldDirty: false });
      }
    }
    // For editing, the default values function already sets the preset's VAT rate
  }, [companyVatRates, preset, type, setValue]);

  useEffect(() => {
    setValue('color', offerColor, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerColor]);

  useEffect(() => {
    if (companyChannels) {
      const channelsFormatted = companyChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          checked: true,
          value: '',
        }));
      setAdmissionChannels(channelsFormatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyChannels]);

  useEffect(() => {
    const findChannelById = (id: string, channelArray: ActiveChannelRequest) => {
      return channelArray.channels.filter((element) => element.id === id);
    };

    if (companyChannels && activeChannels) {
      const channelsFormatted = companyChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          checked: true,
          value: '',
        }));

      const isChannelQuantityDefined =
        activeChannels.channels.some((channel) => !!channel.maxQuantity) ||
        !!preset?.settings.channelDefaultMaxQuantity;

      if (isChannelQuantityDefined) setValue('salesQuota', CHANNELS_SALES_QUOTA_CHANNEL);
      else setValue('salesQuota', CHANNELS_SALES_QUOTA_UNLIMITED);

      channelsFormatted.forEach((channel) => {
        channel.checked = false;
        channel.value = '';
        const currentActiveChannel = findChannelById(channel.id, activeChannels);

        if (currentActiveChannel[0]) {
          channel.checked = true;
          channel.value = `${currentActiveChannel[0].maxQuantity}`;
        } else if (activeChannels.isForAllChannels) {
          channel.checked = true;
          channel.value = preset?.settings.channelDefaultMaxQuantity?.toString() ?? '';
        }
      });

      if (isChannelQuantityDefined || !!activeChannels.channels.length) setValue('isForAllChannels', true);

      setAdmissionChannels(channelsFormatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannels, companyChannels]);

  const price = watch('price');

  useEffect(() => {
    if (Number(price) < Number(getValues('onlinePrice'))) {
      setAcceptDownPayment(false);
      setValue('onlinePrice', '', { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price]);

  const onSubmit: SubmitHandler<Form> = async (form) => {
    const formDirty = getDirtyFields(dirtyFields, form);
    const isChannelCreatedOrDirty =
      !!dirtyFields.isChannelsDirty || !!dirtyFields.isForAllChannels || !!dirtyFields.salesQuota || !preset;

    const ticket: CreateAdmissionPresetRequest | null = getTicketFromForm({
      form: preset ? formDirty : form,
      offer: preset,
      type,
      salesQuota: form.salesQuota ?? '',
      currency: venueCurrency,
      companyClubId: companyClubState.id,
      ...(isChannelCreatedOrDirty && { admissionChannels }),
    });

    if (!ticket) {
      return;
    }

    handleFormSubmit(ticket, type);
  };

  const languageOptions = useMemo(
    () =>
      LANGUAGES.map(({ id, value }) => ({
        id,
        value: t(value),
      })),
    [t]
  );

  const handleEditAdmissionName = () => {
    setIsTitleModalOpen(true);
  };

  const isSubmitButtonDisabled = useMemo(() => {
    return !isValid;
  }, [isValid]);

  const handleChangeOfferName = (name: Translations) => {
    setValue('name', name, { shouldDirty: true });
    setIsTitleModalOpen(false);
  };

  const defaultOfferNameTranslation = getDefaultOfferNameTranslation(type);
  const offerName =
    getTranslatedPropertyBasedOnOrder({
      translations: getValues('name') as Translations,
      preferredLanguage: i18n.language,
    }) ?? t(defaultOfferNameTranslation);

  const quantityRegister = register(FIELD_QUANTITY, {
    validate: {
      cannotBeNegative: (value) =>
        cannotBeNegative({
          value,
          message: t('offers:admissionTicketQuantityNegativeError'),
        }),
      maxDecimals: (value) =>
        maxDecimals({
          value,
          decimals: 0,
          message: t('offers:admissionTicketQuantityDecimalsError'),
        }),
    },
  });

  const validateMaxItemsPerTransaction = () => {
    const quantity = getValues(FIELD_QUANTITY) ? Number(getValues(FIELD_QUANTITY)) : null;
    const maxItems = Number(getValues('itemsPerTransaction.max'));

    const hasItemsPerTransactionMaxErrors = !!errors.itemsPerTransaction?.max?.message;

    const maxValue = calculateMaxItemsPerTransation(quantity);

    const maxItemsPerTransactionMessage = max({
      value: maxItems,
      maxValue,
      message: `Max items per transaction cannot be grater than ${maxValue}`,
    });

    if (!hasItemsPerTransactionMaxErrors && maxItemsPerTransactionMessage) {
      setError('itemsPerTransaction.max', {
        type: 'max',
        message: maxItemsPerTransactionMessage,
      });
      return;
    }

    if (
      hasItemsPerTransactionMaxErrors &&
      errors.itemsPerTransaction?.max?.type === 'max' &&
      !maxItemsPerTransactionMessage
    ) {
      clearErrors('itemsPerTransaction.max');
      return;
    }

    if (!hasItemsPerTransactionMaxErrors) {
      clearErrors('itemsPerTransaction.max');
    }
  };

  const handleSetIsTitleModalClose = () => {
    setIsTitleModalOpen(false);
  };

  const handleCloseColorPickerModal = () => {
    setIsColorPickerModalOpen(false);
  };

  const handleSubmitColorPicker = (color: string) => {
    setOfferColor(color);
    setIsColorPickerModalOpen(false);
  };

  const Icon = preset?.color ? icon : IconBucket;

  return (
    <>
      <ModalV2 isOpen={isColorPickerModalOpen} onClose={handleCloseColorPickerModal}>
        <ColorPickerModal
          imgSrc={floorplan?.imageUrl ?? ''}
          handleClose={handleCloseColorPickerModal}
          handleSubmit={handleSubmitColorPicker}
          defaultColor={offerColor}
        />
      </ModalV2>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalStickyHeader
            bordered
            title={offerName}
            titleDataTestId={DATA_TESTID_ADMISSION_MODAL_TICKET_STEP_TITLE}
            primaryIcon={Icon}
            primaryIconClassName={iconClassName}
            primaryIconColor={offerColor}
            onClickPrimaryIcon={() => setIsColorPickerModalOpen(true)}
            onClickEditTitle={handleEditAdmissionName}
          />

          <div className='mx-4 sm:mx-7 my-8'>
            <div className={`grid ${gridWidth} gap-6 items-end md:items-baseline`}>
              <div>
                {type === ADMISSION_TYPE_BOTTLE_SERVICE ? (
                  <LabelInfo htmlFor='price' hasError={!!errors.price} text={t('offers:bottleServicePriceTooltip')}>
                    {t('offers:admissionPriceLabel')}
                  </LabelInfo>
                ) : (
                  <LabelPrimary htmlFor='price' hasError={!!errors.price}>
                    {t('offers:admissionPriceLabel')}
                  </LabelPrimary>
                )}
                <InputPrice
                  id='price'
                  type='number'
                  placeholder={t('offers:admissionPricePlaceholder')}
                  min='0'
                  step='.01'
                  currency={venueCurrency}
                  hasError={!!errors.price}
                  {...register('price', {
                    required: t(REQUIRED_TEXT_ERROR),
                    validate: {
                      cannotBeNegative: (value) =>
                        cannotBeNegative({
                          value,
                          message: t('offers:admissionPriceCannotBeNegativeError'),
                        }),
                      maxDecimals: (value) =>
                        maxDecimals({
                          value,
                          message: t('offers:admissionPriceDecimalsError'),
                          decimals: 2,
                        }),
                    },
                  })}
                />
                <ErrorMessage name='price' />
              </div>

              {isDdpActive && (
                <div>
                  <LabelPrimary htmlFor='ddp' hasError={!!errors.ddp}>
                    {t('offers:admissionDDPLabel')}
                  </LabelPrimary>
                  <Input
                    id='ddp'
                    type='number'
                    placeholder={t('offers:admissionPricePlaceholder')}
                    min='0'
                    step='.01'
                    hasError={!!errors.ddp}
                    {...register('ddp', {
                      validate: {
                        cannotBeNegative: (value) =>
                          cannotBeNegative({
                            value,
                            message: t('offers:admissionDDPCannotBeNegativeError'),
                          }),
                        maxDecimals: (value) =>
                          maxDecimals({
                            value,
                            message: t('offers:admissionDDPDecimalsError'),
                            decimals: 2,
                          }),
                      },
                    })}
                  />
                  <ErrorMessage name='ddp' />
                </div>
              )}

              {isDrinksIncludedActive && (
                <div>
                  <LabelPrimary htmlFor='drinksIncluded' hasError={!!errors.drinksIncluded}>
                    {t('offers:admissionDrinksIncludedLabel')}
                  </LabelPrimary>
                  <Input
                    id='drinksIncluded'
                    type='number'
                    placeholder={t('offers:admissionDrinksIncludedPlaceholder')}
                    min='0'
                    step='1'
                    hasError={!!errors.drinksIncluded}
                    {...register('drinksIncluded', {
                      validate: {
                        cannotBeNegative: (value) =>
                          cannotBeNegative({
                            value,
                            message: t('offers:admissionDrinksIncludedCannotBeNegativeError'),
                          }),
                        maxDecimals: (value) =>
                          maxDecimals({
                            value,
                            message: t('offers:admissionDrinksIncludedDecimalsError'),
                            decimals: 0,
                          }),
                      },
                    })}
                  />
                  <ErrorMessage name='drinksIncluded' />
                </div>
              )}

              <div>
                <LabelPrimary htmlFor={FIELD_QUANTITY} hasError={!!errors.quantity}>
                  {t('offers:admissionQuantityLabel')}
                </LabelPrimary>

                <Input
                  id={FIELD_QUANTITY}
                  type='number'
                  placeholder={t('offers:admissionQuantityPlaceholder')}
                  step={1}
                  min='0'
                  hasError={!!errors.quantity}
                  {...quantityRegister}
                  onChange={(e) => {
                    quantityRegister.onChange(e);
                    validateMaxItemsPerTransaction();
                  }}
                />
                <ErrorMessage name={FIELD_QUANTITY} />
              </div>

              <div>
                <LabelPrimary htmlFor='itemsPerTransaction.max' hasError={!!errors.itemsPerTransaction?.max}>
                  {t('offers:admissionMaxItemsPerTransactionLabel')}
                </LabelPrimary>

                <Input
                  id='itemsPerTransaction.max'
                  type='number'
                  step={1}
                  min='1'
                  max={DEFAULT_MAX_ITEMS_PER_TRANSACTION}
                  placeholder={type === ADMISSION_TYPE_GUEST_LIST ? '10' : '5'}
                  hasError={!!errors.itemsPerTransaction?.max}
                  {...register('itemsPerTransaction.max', {
                    validate: {
                      cannotBeNegative: (value) =>
                        cannotBeNegative({
                          value,
                          message: t('offers:admissionMaxItemsPerTransactionNegativeError'),
                        }),
                      maxDecimals: (value) =>
                        maxDecimals({
                          value,
                          decimals: 0,
                          message: t('offers:admissionMaxItemsPerTransactionDecimalsError'),
                        }),
                      max: () => {
                        const quantity = getValues(FIELD_QUANTITY) ? Number(getValues(FIELD_QUANTITY)) : null;

                        const maxItems = getValues('itemsPerTransaction.max');

                        const maxValue = calculateMaxItemsPerTransation(quantity);

                        return max({
                          value: maxItems,
                          maxValue,
                          message: t('offers:admissionMaxItemsPerTransactionMaxError', {
                            maxValue,
                          }),
                        });
                      },
                    },
                  })}
                />
                <ErrorMessage name='itemsPerTransaction.max' />
              </div>
            </div>

            <div className='mt-8'>
              <div className='flex justify-between items-center'>
                <LabelPrimary htmlFor='description' hasError={!!errors.description}>
                  {t('offers:admissionOfferDescriptionLabel')}
                </LabelPrimary>
                <div className='flex justify-end'>
                  <div className='w-36'>
                    <Controller
                      name='descriptionLanguage'
                      control={control}
                      render={({ field }) => (
                        <Listbox
                          {...field}
                          onChange={(value) => {
                            field.onChange(value);
                            setSelectedLanguage(value as string);
                          }}
                          options={languageOptions}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              {LANGUAGES.map((lang) => {
                if (lang.id !== selectedLanguage) {
                  return null;
                }

                const name = `description.${lang.id}`;

                const hasError =
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error — dynamic field name not inferrable by RHF
                  !!errors.description?.[lang.id] || false;

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error — dynamic field name not inferrable by RHF
                const descriptionField = watch(name);
                const length = descriptionField?.length || 0;

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error — dynamic field name not inferrable by RHF
                const textAreaRegister = register(name, {
                  maxLength: DESCRIPTION_MAX_LENGTH,
                });

                return (
                  <Fragment key={`description-${lang.id}`}>
                    <div className={'mt-1'}>
                      <TextareaCounter
                        id={name}
                        rows={5}
                        placeholder={t('offers:admissionOfferDescriptionPlaceholder')}
                        length={length}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        hasError={hasError}
                        charactersText={t('common:characters')}
                        {...textAreaRegister}
                      />
                    </div>
                    <ErrorMessage name={name} />
                  </Fragment>
                );
              })}
            </div>

            {type === ADMISSION_TYPE_BOTTLE_SERVICE && (
              <div className='flex mt-8'>
                <div className='w-full mr-6 lg:w-60 lg:mr-8'>
                  <div>
                    <LabelPrimary htmlFor='guestCapacity' hasError={!!errors.guestCapacity} className='mb-3.5'>
                      {t('offers:paymentsConfigurationLabel')}
                    </LabelPrimary>
                    <InputCheckbox
                      name={'acceptDownPayment'}
                      label={t('offers:acceptDownpayments')}
                      value={acceptDownPayment}
                      onChange={(value) => {
                        setAcceptDownPayment(value);
                        if (!value)
                          setValue('onlinePrice', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                      }}
                    />
                  </div>
                </div>
                <div className='w-full lg:w-auto'>
                  <div className='md:w-40 lg:w-61.5'>
                    <LabelInfo
                      htmlFor='guestCapacity'
                      hasError={!!errors.guestCapacity}
                      text={t('offers:admissionGuestCapacityTooltip')}>
                      {t('offers:admissionGuestCapacityLabel')}
                    </LabelInfo>

                    <Input
                      id='guestCapacity'
                      type='number'
                      step={1}
                      min='1'
                      placeholder={t('offers:admissionGuestCapacityPlaceholder')}
                      hasError={!!errors.guestCapacity}
                      {...register('guestCapacity', {
                        required: t('offers:admissionGuestCapacityRequiredError') as string,
                        validate: {
                          cannotBeNegative: (value) =>
                            cannotBeNegative({
                              value,
                              message: t('offers:admissionGuestCapacityNegativeError'),
                            }),
                          maxDecimals: (value) =>
                            maxDecimals({
                              value,
                              decimals: 0,
                              message: t('offers:admissionGuestCapacityDecimalsError'),
                            }),
                          min: (value) =>
                            min({
                              value,
                              minValue: 1,
                              message: t('offers:admissionGuestCapacityMinError'),
                            }),
                        },
                      })}
                    />
                    <ErrorMessage name='guestCapacity' />
                  </div>
                </div>
              </div>
            )}
            {acceptDownPayment && (
              <div className='mt-4 grid grid-cols-1'>
                <div>
                  <LabelPrimary htmlFor='offlinePrice' hasError={!!errors.onlinePrice}>
                    {t('offers:downpayment')}
                  </LabelPrimary>
                </div>
                <div className='flex'>
                  <div className='md:w-40 lg:w-37.5 mr-6'>
                    <InputPrice
                      id='offlinePrice'
                      type='number'
                      placeholder={t('offers:admissionPricePlaceholder')}
                      min='1'
                      step='.01'
                      currency={venueCurrency}
                      hasError={!!errors.onlinePrice}
                      {...register('onlinePrice', {
                        validate: {
                          cannotBeNegative: (value) =>
                            cannotBeNegative({
                              value,
                              message: t('offers:admissionPriceCannotBeNegativeError'),
                            }),
                          maxDecimals: (value) =>
                            maxDecimals({
                              value,
                              message: t('offers:admissionPriceDecimalsError'),
                              decimals: 2,
                            }),
                          max: (value) => {
                            const maxValue = getValues('price');

                            return max({
                              value,
                              maxValue,
                              message: t('offers:downpaymentError', {
                                maxValue,
                              }),
                            });
                          },
                          min: (value) => {
                            if (value != '')
                              return min({
                                value,
                                minValue: 1,
                                message: t('offers:downpaymentMinError'),
                              });
                          },
                        },
                      })}
                    />
                    <ErrorMessage name='offlinePrice' />
                  </div>
                  <div className='flex lg:col-span-2 items-center text-left text-base mt-1 md:mt-0'>
                    <p className='text-subtitle-s text-primary'>
                      {t('offers:totalPrice')}:{' '}
                      {formatCurrency({
                        currency: preset?.price.currency,
                        number: Number(watch('price')),
                      })}{' '}
                      <span className='text-body-s text-tertiary'>
                        {`(${formatCurrency({
                          currency: preset?.price.currency,
                          number: Number(watch('onlinePrice')),
                        })} ${t('offers:downpayment').toLocaleLowerCase()} + ${formatCurrency({
                          currency: preset?.price.currency,
                          number: Number(watch('price')) - Number(watch('onlinePrice')),
                        })} ${t('offers:onPremise')})`}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <AdvancedForm
              setAdmissionsChannels={setAdmissionChannels}
              admissionChannels={admissionChannels}
              clubState={clubState}
              type={type}
              locale={locale}
            />
          </div>
          <ModalStickyFooter
            isSubmit
            isSubmitDisabled={isSubmitButtonDisabled}
            primaryButtonText={preset ? t('common:save') : t('common:create')}
            secondaryButtonText={t('common:discard')}
            onSecondaryButtonClick={handleClose}
          />
        </form>
      </FormProvider>
      <EditFieldModal
        isOpen={isTitleModalOpen}
        onClose={handleSetIsTitleModalClose}
        onSave={handleChangeOfferName}
        defaultValue={getValues('name') as Translations}
      />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title={t('offers:admissionModalConfirmTitle')}
        handleClickConfirm={handleSaveConfirmModal}
        handleClose={handleCloseConfirmModal}
      />
    </>
  );
};
